import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { reviews } from "@/mocks/data";

export default function About() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormSuccess(true);
      form.reset();
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch("https://readdy.ai/api/form/d9qt4p6e3oq4jqumtqd0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormSuccess(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && (serverMsg.includes("spam") || serverMsg.includes("form data is spam"))) {
          setFormError("提交失败，请稍后再试。");
        } else {
          setFormError(serverMsg || "提交失败，请稍后再试。");
        }
      }
    } catch {
      setFormError("网络错误，请检查连接后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "红桃影视大全",
    url: "https://www.hongtao-tv.com",
    description: "红桃影视大全高清欧美剧汇聚海量欧美热门剧集，提供1080P超清画质、中英双语字幕。",
    address: {
      "@type": "PostalAddress",
      addressLocality: "北京",
      addressRegion: "北京市",
      addressCountry: "CN",
      streetAddress: "朝阳区传媒产业园",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "400-888-8888",
      contactType: "customer service",
      availableLanguage: ["Chinese"],
      email: "contact@hongtao-tv.com",
    },
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于红桃影视大全 - 高清欧美剧在线观看平台",
    description: "了解红桃影视大全的品牌故事、核心优势与用户口碑。我们致力于提供最优质的欧美剧在线观看体验。",
    url: "https://www.hongtao-tv.com/about",
    isPartOf: {
      "@type": "WebSite",
      name: "红桃影视大全",
      url: "https://www.hongtao-tv.com",
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="mx-auto px-4 md:px-6 max-w-5xl">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: "关于我们" },
              ]}
            />

            <section className="mt-6 mb-12">
              <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 mb-4">
                关于<span className="text-primary-500">红桃影视大全</span>
              </h1>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">品牌故事</h2>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                      红桃影视大全创立于2020年，是国内领先的欧美剧在线观看平台。我们深知国内观众对高质量欧美剧集的渴求，因此汇聚了一支由影视爱好者组成的内容团队，致力于为广大剧迷提供最新、最全、最高清的欧美剧资源。
                    </p>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                      经过多年的深耕与发展，红桃影视大全已积累超过5000部欧美剧集资源，涵盖悬疑、动作、爱情、科幻、喜剧、恐怖等全类型。我们的字幕团队由资深翻译组成，确保每一部剧集的中英双语字幕都准确、流畅、接地气。
                    </p>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      1080P超清画质是我们的标配。我们相信，只有极致的观影体验才能配得上优秀的欧美剧集。无论你是在电脑、平板还是手机上观看，都能享受流畅、清晰、无广告的观剧体验。
                    </p>
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20stylish%20home%20entertainment%20setup%20with%20large%20screen%20displaying%20cinematic%20scene%2C%20cozy%20living%20room%20with%20warm%20ambient%20lighting%2C%20minimalist%20interior%20design%2C%20professional%20photography%2C%20soft%20crimson%20and%20amber%20accents%2C%20luxurious%20yet%20inviting%20atmosphere%2C%20high%20detail%2C%20clean%20composition&width=800&height=600&seq=about-brand-01&orientation=landscape"
                      alt="红桃影视大全品牌形象"
                      title="红桃影视大全 - 品牌故事"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-6 text-center">核心优势</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { icon: "ri-movie-line", title: "海量资源", desc: "5000+欧美剧集，涵盖全类型" },
                    { icon: "ri-4k-line", title: "1080P超清", desc: "全高清画质，每一帧都惊艳" },
                    { icon: "ri-translate", title: "双语字幕", desc: "精准翻译，中英对照学习" },
                    { icon: "ri-speed-up-line", title: "极速更新", desc: "海外播出24小时内上线" },
                  ].map((item, i) => (
                    <div key={i} className="text-center p-4">
                      <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{item.title}</h3>
                      <p className="text-foreground-500 text-xs">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-6 text-center">用户口碑</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-fill text-sm ${
                            i < review.rating ? "text-accent-500" : "text-background-300"
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-4">{review.content}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-foreground-900 font-medium text-sm">{review.name}</span>
                      <span className="text-foreground-400 text-xs">{review.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="contact" className="mb-6">
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-2 text-center">联系我们</h2>
                <p className="text-foreground-500 text-sm text-center mb-6">
                  有任何问题或建议？欢迎给我们留言，我们会尽快回复。
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <h3 className="font-medium text-foreground-900 text-sm">邮箱</h3>
                          <p className="text-foreground-500 text-sm">contact@hongtao-tv.com</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                          <i className="ri-phone-line"></i>
                        </div>
                        <div>
                          <h3 className="font-medium text-foreground-900 text-sm">电话</h3>
                          <p className="text-foreground-500 text-sm">400-888-8888</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <h3 className="font-medium text-foreground-900 text-sm">地址</h3>
                          <p className="text-foreground-500 text-sm">北京市朝阳区传媒产业园A座15层</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                          <i className="ri-time-line"></i>
                        </div>
                        <div>
                          <h3 className="font-medium text-foreground-900 text-sm">工作时间</h3>
                          <p className="text-foreground-500 text-sm">周一至周日 9:00 - 22:00</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    {formSuccess ? (
                      <div className="bg-background-50 rounded-lg p-8 text-center">
                        <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                          <i className="ri-check-line text-2xl"></i>
                        </div>
                        <h3 className="font-heading font-bold text-foreground-900 mb-2">提交成功！</h3>
                        <p className="text-foreground-500 text-sm">感谢您的留言，我们会尽快回复您。</p>
                      </div>
                    ) : (
                      <form
                        onSubmit={handleSubmit}
                        data-readdy-form
                        className="bg-background-50 rounded-lg p-5 space-y-4"
                      >
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                            姓名
                          </label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none bg-background-50 transition-all"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                            邮箱
                          </label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none bg-background-50 transition-all"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                            留言
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            rows={4}
                            maxLength={500}
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none bg-background-50 transition-all resize-none"
                            placeholder="请输入您的留言内容（500字以内）"
                          ></textarea>
                        </div>
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                        {formError && (
                          <p className="text-sm text-red-500">{formError}</p>
                        )}
                        <button
                          type="submit"
                          disabled={submitting}
                          className="w-full px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap text-sm"
                        >
                          {submitting ? "提交中..." : "发送留言"}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}